import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCallback, useEffect, useRef} from 'react'

import {TimelinePanelSkeletonRows} from './TimelinePanelSkeleton'

interface UseInfiniteScrollLoadProps {
  fetchNextPage: () => unknown
  hasMore: boolean
  isFetchingNextPage: boolean
}

export function useInfiniteScrollLoad({fetchNextPage, hasMore, isFetchingNextPage}: UseInfiniteScrollLoadProps) {
  const sentinelRef = useRef<Element | null>(null)

  // Stable callback ref that only stores the node. It must attach to both the flag-off
  // <div> and the flag-on <li> (Primer Timeline.Item), whose ref types differ, so a
  // callback typed (Element | null) is the single shape assignable to both. Observer
  // wiring stays in the effect below (keyed on loadMore) so it never captures a stale
  // loadMore; the ref deliberately does no observer setup.
  const setSentinel = useCallback((el: Element | null) => {
    sentinelRef.current = el
  }, [])

  const loadMore = useCallback(() => {
    if (hasMore && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [fetchNextPage, hasMore, isFetchingNextPage])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) loadMore()
      },
      {threshold: 0.1},
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore])

  useEffect(() => {
    if (isFetchingNextPage) announce('Loading more activity')
  }, [isFetchingNextPage])

  if (!hasMore) return undefined

  // Under primer_react_timeline_list_semantics <Timeline> renders as an <ol>, so the
  // skeleton rows must be <li> children of it directly. A wrapper <div> between the
  // <ol> and its <li> children is invalid markup, so under the flag the rows render
  // with no wrapper, each aria-hidden (a decorative placeholder, excluded from the list
  // count), and the observer ref lands on the first row.
  if (isFeatureEnabled('primer_react_timeline_list_semantics')) {
    return <TimelinePanelSkeletonRows aria-hidden firstRowRef={setSentinel} />
  }

  return (
    <div ref={setSentinel} aria-hidden>
      <TimelinePanelSkeletonRows />
    </div>
  )
}
